import type { Participant } from '@crowdset/contracts';
import { API } from '@crowdset/contracts/constants';
import { can, canAssignRole, canModerateTarget } from '@crowdset/domain';
import {
  Flag,
  Prohibit,
  ShieldCheck,
  SignOut,
  SpeakerSimpleSlash,
  SpeakerSimpleHigh,
  UserCircle,
  UserMinus,
  Eye,
  EyeSlash,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { DropdownMenu } from 'radix-ui';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { usePrefs } from '../../lib/prefs';
import { useCommand } from '../../room/context';
import { useRoomStore } from '../../room/store';
import { cx } from '../../ui/kit';
import { useRoomDialogs } from './dialogs-store';

const item =
  'flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm text-fg-muted outline-none data-[highlighted]:bg-white/[0.07] data-[highlighted]:text-fg data-[disabled]:opacity-40';
const modItem =
  'flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2.5 text-sm text-[#f0c9a0] outline-none data-[highlighted]:bg-warn/10';

/** Actions for one participant. Server re-checks every permission. */
export function UserMenu({
  participant: p,
  children,
}: {
  participant: Participant;
  children: ReactNode;
}) {
  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <UserMenuContent p={p} />
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function UserMenuContent({ p }: { p: Participant }) {
  const data = useRoomStore((s) => s.data)!;
  const me = data.me;
  const nav = useNavigate();
  const qc = useQueryClient();
  const blocked = usePrefs((s) => s.blocked);
  const toggleBlocked = usePrefs((s) => s.toggleBlocked);
  const openDialog = useRoomDialogs((s) => s.open);
  const mute = useCommand('moderation.mute', { success: 'Muted.' });
  const unmute = useCommand('moderation.unmute', { success: 'Mute lifted.' });
  const removeDj = useCommand('moderation.removeDj', { success: 'Removed from the DJ rotation.' });
  const isSelf = p.participantId === me.participantId;
  const target = p.userId ? { userId: p.userId, role: p.role } : null;
  const actor = me.userId ? { userId: me.userId, role: me.role } : null;
  const canMod = !!(actor && target && canModerateTarget(actor, target));
  // Guests have no account to mute or ban, but any moderator can remove them from the room.
  const canKickGuest = !!(actor && p.isGuest && !isSelf && can(actor.role, 'moderate'));
  const canRole = !!(
    actor &&
    target &&
    !p.isGuest &&
    canAssignRole(actor, target, p.role === 'moderator' ? 'member' : 'moderator')
  );
  const inRotation =
    !!p.userId && (data.queue.some((q) => q.user.id === p.userId) || data.play?.dj.id === p.userId);
  const isBlocked = !!p.userId && blocked.includes(p.userId);

  const setRole = async () => {
    try {
      await api.put(API.roomRole(data.room.slug, p.userId!), {
        role: p.role === 'moderator' ? 'member' : 'moderator',
      });
      useRoomStore
        .getState()
        .toast(p.role === 'moderator' ? 'Moderator role removed.' : 'Made moderator.', 'ok');
      void qc.invalidateQueries({ queryKey: ['room', data.room.slug] });
    } catch (e) {
      useRoomStore.getState().toast(errorMessage(e), 'error');
    }
  };

  return (
    <DropdownMenu.Content
      sideOffset={6}
      collisionPadding={12}
      className="z-50 min-w-60 rounded-2xl border border-white/10 bg-ink-850 p-1.5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.7)]"
    >
      <div className="px-2.5 py-2">
        <div className="text-sm font-medium">
          {p.displayName}
          {isSelf && <span className="text-fg-subtle"> (you)</span>}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-fg-subtle">
          {p.handle ? <span className="font-mono">@{p.handle}</span> : <span>Guest listener</span>}
          {p.role !== 'member' && p.role !== 'guest' && (
            <span className="capitalize text-accent">{p.role}</span>
          )}
          {!p.connected && <span>· reconnecting</span>}
        </div>
      </div>
      {p.handle && (
        <DropdownMenu.Item className={item} onSelect={() => nav(`/u/${p.handle}`)}>
          <UserCircle size={16} aria-hidden /> View profile
        </DropdownMenu.Item>
      )}
      {!isSelf && p.userId && me.userId && (
        <>
          <DropdownMenu.Item className={item} onSelect={() => toggleBlocked(p.userId!)}>
            {isBlocked ? <Eye size={16} aria-hidden /> : <EyeSlash size={16} aria-hidden />}
            {isBlocked ? 'Show their chat' : 'Hide their chat (block)'}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={item}
            onSelect={() => openDialog({ kind: 'report', target: p })}
          >
            <Flag size={16} aria-hidden /> Report…
          </DropdownMenu.Item>
        </>
      )}
      {(canMod || canRole || canKickGuest) && (
        <>
          <DropdownMenu.Separator className="my-1 h-px bg-white/[0.07]" />
          <DropdownMenu.Label className="flex items-center gap-1.5 px-2.5 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wider text-warn/80">
            <ShieldCheck size={12} weight="bold" aria-hidden /> Room-wide moderation
          </DropdownMenu.Label>
        </>
      )}
      {canMod && (
        <>
          {[5, 15, 60].map((m) => (
            <DropdownMenu.Item
              key={m}
              className={modItem}
              onSelect={() => void mute.run({ userId: p.userId!, minutes: m, reason: '' })}
            >
              <SpeakerSimpleSlash size={16} aria-hidden /> Mute chat {m} min
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Item
            className={modItem}
            onSelect={() => void unmute.run({ userId: p.userId! })}
          >
            <SpeakerSimpleHigh size={16} aria-hidden /> Lift chat mute
          </DropdownMenu.Item>
          {inRotation && (
            <DropdownMenu.Item
              className={modItem}
              onSelect={() => void removeDj.run({ userId: p.userId!, reason: '' })}
            >
              <UserMinus size={16} aria-hidden /> Remove from DJ rotation
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item
            className={modItem}
            onSelect={() => openDialog({ kind: 'kick', target: p })}
          >
            <SignOut size={16} aria-hidden /> Kick…
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className={cx(modItem, 'text-[#f3a3a4]')}
            onSelect={() => openDialog({ kind: 'ban', target: p })}
          >
            <Prohibit size={16} aria-hidden /> Ban…
          </DropdownMenu.Item>
        </>
      )}
      {canKickGuest && (
        <DropdownMenu.Item
          className={modItem}
          onSelect={() => openDialog({ kind: 'kick', target: p })}
        >
          <SignOut size={16} aria-hidden /> Remove guest from room…
        </DropdownMenu.Item>
      )}
      {canRole && (
        <DropdownMenu.Item className={modItem} onSelect={() => void setRole()}>
          <ShieldCheck size={16} aria-hidden />{' '}
          {p.role === 'moderator' ? 'Remove moderator role' : 'Make moderator'}
        </DropdownMenu.Item>
      )}
    </DropdownMenu.Content>
  );
}
